import { useMemo, useState } from 'react';

import {
  useDeleteInvitation,
  useInvitations,
  useUsers,
} from '../../hooks/useUserManagement';

import { InviteUserModal } from './InviteUserModal';

type TabType = 'all' | 'pending' | 'activated';

/**
 * User Management Page
 * Access: Admin only
 *
 * Allows administrators to:
 * - Create new user invitations
 * - View all users and invitations
 * - Delete pending invitations
 * - See registration status
 */
export function UsersPage() {
  const [activeTab, setActiveTab] = useState<TabType>('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const { data: invitationsData, isLoading: invitationsLoading } =
    useInvitations();
  const { data: usersData, isLoading: usersLoading } = useUsers();
  const deleteInvitation = useDeleteInvitation();

  const invitations = invitationsData?.invitations ?? [];
  const users = usersData?.users ?? [];

  // Calculate stats
  const stats = useMemo(() => {
    const pendingCount = invitations.filter(
      (inv) => inv.status === 'pending'
    ).length;
    const activatedCount = users.length;
    const totalCount = activatedCount;

    // Calculate deduplicated count for "All Users" tab
    // Count unique users (activated) + pending invitations
    const deduplicatedTotal = activatedCount + pendingCount;

    return {
      total: totalCount,
      pending: pendingCount,
      active: activatedCount,
      deduplicatedTotal,
    };
  }, [invitations, users]);

  // Combine and filter data based on active tab
  const tableData = useMemo(() => {
    // Create a map to deduplicate activated users by userId
    const userMap = new Map<
      string,
      {
        email: string;
        status: 'pending' | 'activated';
        activationCode?: string;
        name?: string;
        roles: string[];
        createdAt: string;
        userId?: string;
      }
    >();

    // First, add all user profile records
    users.forEach((user) => {
      userMap.set(user.userId, {
        email: user.email,
        status: 'activated' as const,
        activationCode: undefined,
        name: user.preferredName || user.familyName,
        roles: user.roles,
        createdAt: user.createdAt,
        userId: user.userId,
      });
    });

    // Then process invitations
    invitations.forEach((inv) => {
      if (inv.userId && userMap.has(inv.userId)) {
        // Merge with existing user record, using invitation email
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion -- userMap.has() guarantees the value exists
        const existingUser = userMap.get(inv.userId)!;
        userMap.set(inv.userId, {
          ...existingUser,
          email: inv.email, // Use invitation email, not placeholder
          activationCode: inv.activationCode,
        });
      } else if (!inv.userId) {
        // Pending invitation - add as separate row using email as key
        userMap.set(`pending-${inv.email}`, {
          email: inv.email,
          status: inv.status,
          activationCode: inv.activationCode,
          name: undefined,
          roles: [],
          createdAt: inv.createdAt,
          userId: inv.userId,
        });
      }
    });

    // Convert map to array
    const combined = Array.from(userMap.values());

    // Filter based on active tab
    if (activeTab === 'pending') {
      return combined.filter((item) => item.status === 'pending');
    } else if (activeTab === 'activated') {
      return combined.filter((item) => item.status === 'activated');
    }

    // Sort by createdAt (newest first)
    return combined.sort(
      (a, b) =>
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
    );
  }, [invitations, users, activeTab]);

  const handleDelete = async (email: string) => {
    if (
      window.confirm(
        `Are you sure you want to delete the invitation for ${email}?`
      )
    ) {
      try {
        setDeleteError(null);
        await deleteInvitation.mutateAsync(email);
      } catch (error) {
        setDeleteError(
          error instanceof Error ? error.message : 'Failed to delete invitation'
        );
      }
    }
  };

  const isLoading = invitationsLoading || usersLoading;

  return (
    <div className="p-8">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-6">
          <h1 className="text-3xl font-bold text-text-dark">User Management</h1>
          <button onClick={() => setIsModalOpen(true)} className="btn-primary">
            + Invite User
          </button>
        </div>

        {/* Stats Cards */}
        <div className="grid gap-6 md:grid-cols-3 mb-8">
          <div className="card text-center">
            <div className="text-4xl font-bold mb-2 text-primary">
              {stats.total}
            </div>
            <div className="text-text-secondary">Total Users</div>
          </div>
          <div className="card text-center">
            <div className="text-4xl font-bold mb-2 text-accent-orange">
              {stats.pending}
            </div>
            <div className="text-text-secondary">Pending Invitations</div>
          </div>
          <div className="card text-center">
            <div className="text-4xl font-bold mb-2 text-secondary">
              {stats.active}
            </div>
            <div className="text-text-secondary">Active Users</div>
          </div>
        </div>

        {/* Tabs */}
        <div className="card">
          {deleteError && (
            <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700">{deleteError}</p>
              <button
                onClick={() => setDeleteError(null)}
                className="mt-2 text-sm text-red-600 hover:text-red-800 underline"
              >
                Dismiss
              </button>
            </div>
          )}
          <div className="flex border-b border-border-light mb-6">
            <button
              onClick={() => setActiveTab('all')}
              className={`px-4 py-2 font-medium -mb-px ${
                activeTab === 'all'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-text-secondary hover:text-text-dark'
              }`}
            >
              All Users ({stats.deduplicatedTotal})
            </button>
            <button
              onClick={() => setActiveTab('pending')}
              className={`px-4 py-2 font-medium -mb-px ${
                activeTab === 'pending'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-text-secondary hover:text-text-dark'
              }`}
            >
              Pending Invitations ({stats.pending})
            </button>
            <button
              onClick={() => setActiveTab('activated')}
              className={`px-4 py-2 font-medium -mb-px ${
                activeTab === 'activated'
                  ? 'text-primary border-b-2 border-primary'
                  : 'text-text-secondary hover:text-text-dark'
              }`}
            >
              Activated ({stats.active})
            </button>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="text-center py-12 text-text-secondary">
              <p>Loading...</p>
            </div>
          ) : tableData.length === 0 ? (
            <div className="text-center py-12 text-text-secondary">
              <p>
                {activeTab === 'all'
                  ? 'No users yet. Start by inviting your first user.'
                  : activeTab === 'pending'
                    ? 'No pending invitations.'
                    : 'No activated users yet.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full">
                <thead>
                  <tr className="border-b border-border-light">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-dark">
                      Email
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-dark">
                      Name
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-dark">
                      Status
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-dark">
                      Roles
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-dark">
                      Created
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-text-dark">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {tableData.map((row) => (
                    <tr
                      key={row.userId || `pending-${row.email}`}
                      className="border-b border-border-light hover:bg-gray-50"
                    >
                      <td className="px-4 py-3 text-sm text-text-dark">
                        {row.email}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary">
                        {row.name || '—'}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        <span
                          className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                            row.status === 'activated'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {row.status === 'activated'
                            ? '✓ Activated'
                            : '⏳ Pending'}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary">
                        {row.roles.length > 0 ? row.roles.join(', ') : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-text-secondary">
                        {new Date(row.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {row.status === 'pending' ? (
                          <button
                            onClick={() => handleDelete(row.email)}
                            disabled={deleteInvitation.isPending}
                            className="text-red-600 hover:text-red-800 disabled:opacity-50"
                          >
                            Delete
                          </button>
                        ) : (
                          <span className="text-text-secondary">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      <InviteUserModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
}
